<template>
  <nav
    :class="{
      AppSidebar: true,
      'AppSidebar--header': showHeader,
      'AppSidebar--collapsed': mobileMode && isSidebarCollapsed,
      'AppSidebar--student': isAuthenticated && !isVolunteer,
    }"
  >
    <div class="AppSidebar-content">
      <div class="uc-column">
        <!--
          P3 final shell cleanup (00_*.md §13.x, PF-15 defect 2): the
          rendered prototype's sidebar begins with the Borna brand block, not
          a user-profile identity block. Scoped to active-student only --
          volunteer keeps the exact original SidebarInfo (avatar/name/role/
          status) it always had.
        -->
        <sidebar-brand v-if="!mobileMode && isAuthenticated && !isVolunteer" />
        <sidebar-info
          v-else-if="!mobileMode"
          style="margin-bottom: 64px;"
          :authenticated="isAuthenticated"
          :isVolunteer="isVolunteer"
          :name="user.firstname"
        />

        <sidebar-links
          :authenticated="isAuthenticated"
          :isVolunteer="isVolunteer"
          :isAdmin="user.isAdmin"
          :mobileMode="mobileMode"
          :logout="logout"
        />
      </div>

      <!--
        P3 full-shell visual remediation (00_*.md §13): volunteer only.
        Student logout is now a nav-embedded row in SidebarLinks, matching
        the rendered prototype's `.nav-item--logout` (00_*.md §13.1) instead
        of this separate legacy footer block.

        Borna bug batch 1 (item 4): this volunteer footer link deliberately
        keeps its DIRECT logout (`volunteerLogout`), without the Borna V2
        confirmation dialog. The dialog is student V2 chrome, and the
        volunteer/teacher shell is frozen (00_*.md §13.2, PF-13). Both paths
        still call the ONE `AuthService.logout` and both now land on the new
        Borna login -- only the confirmation step is student-scoped.
      -->
      <div
        v-if="isAuthenticated && isVolunteer"
        :class="finalLinkClass"
        v-on:click="volunteerLogout"
        v-on:keydown.enter="volunteerLogout"
        :tabindex="isSidebarCollapsed && mobileMode ? -1 : 0"
      >
        {{ $t('appSidebar.logout') }}
      </div>
    </div>
  </nav>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import AuthService from '@/services/AuthService'
import SidebarInfo from './SidebarInfo.vue'
import SidebarBrand from './SidebarBrand.vue'
import SidebarLinks from './SidebarLinks.vue'
import studentLogout from '@/utils/student-logout'
import config from '../../../config'

export default {
  components: { SidebarInfo, SidebarBrand, SidebarLinks },
  mixins: [studentLogout],
  computed: {
    ...mapState({
      user: state => state.user.user,
      showHeaderRaw: state => state.app.header.isShown,
      headerComponent: state => state.app.header.component,
      isSidebarCollapsed: state => state.app.sidebar.isCollapsed,
    }),
    ...mapGetters({
      isAuthenticated: 'user/isAuthenticated',
      isVolunteer: 'user/isVolunteer',
      mobileMode: 'app/mobileMode',
    }),
    // P3 final shell cleanup (00_*.md §13.x, PF-15 defect 1): same
    // generic-masthead suppression as `App/index.vue`'s `showHeader` --
    // duplicated here (not shared via a getter) because this is the same
    // small, self-contained rule each component already evaluates from its
    // own mapped store state. Keeps `AppSidebar--header`'s header-height
    // padding-top in sync with whether AppHeader is actually rendered, so
    // the sidebar's own top offset never drifts from the main content's.
    showHeader() {
      if (!this.showHeaderRaw) return false
      // WP-04V-C (root cause R5). This list has to stay identical to
      // `App/index.vue`'s `showHeader`, which already covers BOTH student
      // routes that render their own `StudentAppHeader`. Only `DashboardView`
      // was listed here, so on `/student/ai-tutor` the sidebar still reserved
      // `bind-app-header-height` for a legacy masthead that is not rendered --
      // pushing the whole sidebar down by a measured 64px on that route alone
      // (the deviation doc 11 recorded as "logo ~63px lower, cause unproven").
      //
      // WP-04V final closeout: 'SessionHistoryView' and 'ProfileView' added, in
      // lockstep with `App/index.vue`, for the same reason.
      //
      // WB-M1A: 'StudentAiTutorBoardView' added, in lockstep again.
      const rendersOwnStudentAppHeader =
        this.$route.name === 'DashboardView' ||
        this.$route.name === 'StudentAiTutorView' ||
        this.$route.name === 'StudentAiTutorBoardView' ||
        this.$route.name === 'SessionHistoryView' ||
        this.$route.name === 'ProfileView'
      const isStudentGenericMasthead =
        rendersOwnStudentAppHeader && !this.isVolunteer && !this.headerComponent
      return !isStudentGenericMasthead
    },
    finalLinkClass() {
      return {
        'AppSidebar-final-link': true,
        'AppSidebar-final-link--desktop': !this.mobileMode,
      }
    },
  },
  methods: {
    // Borna bug batch 1 (item 4): the STUDENT nav row's logout. Raises the one
    // shared Borna confirmation; the shell root runs the single
    // `AuthService.logout` on confirm.
    logout() {
      this.requestLogout()
    },
    // The volunteer footer link's logout: unchanged behaviour (no dialog), same
    // single `AuthService.logout`. See the template comment above.
    volunteerLogout() {
      AuthService.logout(this)
    },
    backToWebsite() {
      window.location = config.mainWebsiteUrl
    },
  },
}
</script>

<style lang="scss" scoped>
$transition: transform 700ms;

.AppSidebar {
  @include bind-app-sidebar-width(width);

  // P3 full-shell visual remediation (00_*.md §13): exact rendered-prototype
  // sidebar width for the student shell only (00_*.md §13.1, `.sidebar`,
  // 246px) -- overrides the shared 250px default at the same breakpoint the
  // mixin above already uses. Volunteer keeps the shared 250px default.
  &.AppSidebar--student {
    @include breakpoint-above('medium') {
      width: 246px;
    }
  }

  background: white;
  height: 100%;
  overflow-y: auto;

  position: fixed;
  top: 0;
  left: 0;
  z-index: get-z('sidebar');

  // transition: $transition ease-out;

  &--header {
    @include bind-app-header-height(padding-top);
  }

  &--collapsed {
    transform: translateY(-100%);
    // transition: $transition ease-in;
  }
}

.AppSidebar-content {
  height: 100%;
  padding: 40px 20px 40px 30px;
  position: relative;

  @media screen and (min-width: get-breakpoint('medium')) and (max-width: get-breakpoint('huge')) and (orientation: landscape) {
    padding-left: 2em;
    height: initial;
  }
}

// P3 visual-parity remediation (student-foundation-redesign, 00_*.md §12):
// tighter, prototype-matched padding for the student sidebar only
// (00_*.md §12 PF-09, `.sidebar`). Volunteer keeps `.AppSidebar-content`'s
// original padding above untouched.
//
// P3 full-shell remediation (00_*.md §13): also a flex column here, from the
// rendered prototype's sidebar (00_*.md §13.1).
// Scoped to `.AppSidebar--student` only -- `.uc-column` is a shared utility
// class, but this rule (scoped to this component) only ever matches the
// instance this component's own template renders.
.AppSidebar--student .AppSidebar-content {
  // P3 final shell cleanup (00_*.md §13.x, PF-15): exact rendered-prototype
  // `.sidebar` padding (60px 20px 20px), measured via CDP -- the top value
  // in particular matters now that the brand block (SidebarBrand.vue)
  // occupies this top offset instead of the removed legacy masthead +
  // SidebarInfo. Symmetric left/right, so the RTL override below carries
  // the same values.
  padding: 60px 20px 20px 20px;
  display: flex;
  flex-direction: column;

  > .uc-column {
    flex: 1;
    min-height: 0;

    // SidebarLinks' own root element -- Vue's scoped-CSS attribute still
    // reaches a child component's root node, so this only ever matches the
    // SidebarLinks instance this component's own template renders.
    > .SidebarLinks {
      flex: 1;
      min-height: 0;
    }
  }
}

.AppSidebar-final-link {
  @include font-category('display-small');
  cursor: pointer;
  padding: 40px 0 60px;
  text-align: left;

  &--desktop {
    @include font-category('button');

    @media screen and (min-width: get-breakpoint('medium')) and (max-width: get-breakpoint('huge')) and (orientation: landscape) {
      position: initial;
      text-align: left;
      margin: 4em 0 1em;
    }
  }
}

html.is-rtl {
  .AppSidebar {
    right: 0;
    left: unset;
  }

  .AppSidebar-content {
    padding: 40px 30px 40px 20px;

    @media screen and (min-width: get-breakpoint('medium')) and (max-width: get-breakpoint('huge')) and (orientation: landscape) {
      padding-right: 2em;
      padding-left: unset;
    }
  }

  .AppSidebar--student .AppSidebar-content {
    padding: 60px 20px 20px 20px;
  }

  .AppSidebar-final-link {
    text-align: right;

    &--desktop {
      @media screen and (min-width: get-breakpoint('medium')) and (max-width: get-breakpoint('huge')) and (orientation: landscape) {
        text-align: right;
      }
    }
  }
}
</style>
